import React, { useMemo } from "react";
import {
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import { getWatchFieldsForEntity } from "../../data/notificationEntities";
import {
  buildWatchFieldSelectOptions,
  getWatchFieldSelectOption,
} from "../mca/mcaWatchFieldLabels";
import { getNotificationWatchFieldValueOptions, coerceWatchFieldValueForSelect } from "../../helpers/notificationWatchFieldValueOptions";
import { formatFormFieldDisplay } from "../../helpers/formatFormFieldDisplay";
import FormReadOnlyField from "../formReadOnlyField";

export const buildNotificationWatchFieldSelectData = (entityKey, currentField) => {
  const paths = getWatchFieldsForEntity(entityKey);
  const options = buildWatchFieldSelectOptions(entityKey, paths);
  const hasCurrent =
    currentField && options.some((option) => option.value === currentField);
  if (currentField && !hasCurrent) {
    const fallback = getWatchFieldSelectOption(entityKey, currentField);
    if (fallback) return [...options, fallback];
  }
  return options;
};

export const buildWatchFieldValueSelectData = (entityKey, field, currentValue) => {
  const options = getNotificationWatchFieldValueOptions(entityKey, field);
  if (!options) return null;
  const normalizedValue = coerceWatchFieldValueForSelect(currentValue, options);
  const data = [...options];
  if (
    normalizedValue !== "" &&
    normalizedValue != null &&
    !data.some((option) => option.value === String(normalizedValue))
  ) {
    data.push({
      value: String(normalizedValue),
      label: String(normalizedValue),
    });
  }
  return { data, normalizedValue };
};

const WatchFieldValueInput = ({
  label,
  entityKey,
  field,
  value,
  onChange,
}) => {
  const selectConfig = buildWatchFieldValueSelectData(entityKey, field, value);
  if (selectConfig) {
    return (
      <Select
        label={label}
        placeholder="Optional"
        size="sm"
        style={{ flex: 1 }}
        data={selectConfig.data}
        value={selectConfig.normalizedValue || null}
        onChange={(v) => onChange(v ?? "")}
        searchable
        clearable
        withinPortal
        disabled={!field}
      />
    );
  }

  return (
    <TextInput
      label={label}
      placeholder="Optional"
      size="sm"
      style={{ flex: 1 }}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={!field}
    />
  );
};

const NotificationWatchFieldsEditor = ({
  rows = [],
  onChange,
  entityKey = null,
  readOnly = false,
}) => {
  const fieldOptionsByRow = useMemo(
    () =>
      rows.map((row) =>
        buildNotificationWatchFieldSelectData(entityKey, row.field ?? "")
      ),
    [entityKey, rows]
  );

  const updateRow = (idx, patch) => {
    const next = rows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };

  const formatWatchValue = (field, value) => {
    const selectConfig = buildWatchFieldValueSelectData(entityKey, field, value);
    if (selectConfig) {
      return formatFormFieldDisplay(selectConfig.normalizedValue, {
        kind: "select",
        props: { data: selectConfig.data },
      });
    }
    if (value === "" || value == null) return "-";
    return String(value);
  };

  if (readOnly) {
    if (!rows.length) {
      return <FormReadOnlyField label="Watch Fields" value="-" />;
    }
    return (
      <Stack spacing="sm">
        {rows.map((wf, idx) => {
          const fieldLabel =
            getWatchFieldSelectOption(entityKey, wf.field)?.label ||
            wf.field ||
            "-";
          return (
            <FormReadOnlyField
              key={idx}
              label={fieldLabel}
              value={`${formatWatchValue(wf.field, wf.fromValue)} → ${formatWatchValue(wf.field, wf.toValue)}`}
            />
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack spacing="sm">
      <Text size="sm" color="dimmed">
        Optionally define fields to monitor with from/to value filters. Leave empty
        to notify on any update.
      </Text>
      {rows.map((wf, idx) => (
        <Group key={idx} align="flex-end" spacing="xs">
          <Select
            label={idx === 0 ? "Field" : undefined}
            placeholder="Select field"
            size="sm"
            style={{ flex: 1 }}
            data={fieldOptionsByRow[idx] ?? []}
            value={wf.field || null}
            onChange={(v) =>
              updateRow(idx, { field: v ?? "", fromValue: "", toValue: "" })
            }
            searchable
            clearable
            withinPortal
            disabled={!entityKey}
          />
          <WatchFieldValueInput
            label={idx === 0 ? "From Value" : undefined}
            entityKey={entityKey}
            field={wf.field}
            value={wf.fromValue ?? ""}
            onChange={(fromValue) => updateRow(idx, { fromValue })}
          />
          <WatchFieldValueInput
            label={idx === 0 ? "To Value" : undefined}
            entityKey={entityKey}
            field={wf.field}
            value={wf.toValue ?? ""}
            onChange={(toValue) => updateRow(idx, { toValue })}
          />
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={() => onChange(rows.filter((_, i) => i !== idx))}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Group>
        <Button
          leftIcon={<IconPlus size={14} />}
          onClick={() =>
            onChange([
              ...rows,
              { field: "", fromValue: "", toValue: "" },
            ])
          }
        >
          Add Watch Field
        </Button>
      </Group>
    </Stack>
  );
};

export default NotificationWatchFieldsEditor;
